import {
  Button,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
} from "@mui/material";
import { Card, Col, Container, Row } from "react-bootstrap";
import React, {
  Fragment,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  createNewStore,
  fetchEditStore,
  fetchStoreList,
} from "../actions/store";
import { useNavigate, useParams } from "react-router-dom";

import Breadcrumb from "../components/Admin/Reusable/Breadcrumb";
import InputField from "../components/Admin/Reusable/InputField";
import SelectStatus from "../components/Admin/Reusable/Form/SelectStatus";
import SubmitButtonForm from "../components/Admin/Reusable/Form/SubmitButtonForm";
import { basicFormValidationHandler } from "../utils";
import { fetchCompanyFormList } from "../actions/company";
import { fetchCountryFormList } from "../actions/country";
import { isEmpty } from "lodash";
import { toast } from "react-toastify";
import { use } from "react";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import FormSystemInformation from "../components/Admin/Reusable/FormSystemInformation";
import FormTitleMain from "../components/Admin/Reusable/FormTitleMain";

const StoreAdd = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();

  const { t } = useTranslation();
  //breadcrumb
  const translateForm = "storesForm";
  const translateModule = "stores";
  const pageType = id ? "Edit" : "Add";
  const breadcrumbItems = [
    { label: t(`breadcrumb.${translateModule}`) },
    {
      label: t(`breadcrumb.${translateModule}${pageType}`),
      active: true,
    },
  ];

  const countryApiCalledRef = useRef(false);

  const initialState = {
    name: "",
    companyId: "",
    status: "ACTIVE",
    email: "",
    address: "",
    phone: "",
  };

  const [data, setData] = useState(initialState);
  const [errors, setErrors] = useState({});
  const [countryValue, setCountryValue] = useState("");

  const { countryFormList } = useSelector((state) => state.countryReducer);
  const { companyFormList } = useSelector((state) => state.companyReducer);
  const { storeDetail } = useSelector((state) => state.storeReducer);

  const handleInputChange = (e) => {
    setData({ ...data, [e.target.name]: e.target.value });
  };

  //ako je korisnik setovan da je admin drzave odmah se nakon dobijanja drzava proverava da li je count 1 i odmah je setuje
  useEffect(() => {
    if (countryFormList.length == 1 && !countryValue) {
      const singleCountry = countryFormList[0];
      setCountryValue(singleCountry.id);
      dispatch(fetchCompanyFormList(singleCountry.id));
    }
  }, [countryFormList, countryValue, dispatch]);

  //pozvati podatke za drzave kako bi izlistali u selektu
  useEffect(() => {
    dispatch(fetchCountryFormList());
  }, [dispatch]);

  useEffect(() => {
    if (!id) {
      // Ako `id` ne postoji, očisti stanje za novu kreaciju
      setData(initialState);
    } else {
      // Ako `id` postoji, učitaj podatke za korisnika
      dispatch(fetchStoreList(id));
    }
  }, [id, dispatch]);

  useEffect(() => {
    if (storeDetail && id) {
      setData({
        ...data,
        name: storeDetail.name || "",
        companyId: storeDetail.companyId || "",
        email: storeDetail.email || "",
        address: storeDetail.address || "",
        phone: storeDetail.phone || "",
        status: storeDetail.status || "ACTIVE",
        createdAt: storeDetail.createdAt || "",
        updatedAt: storeDetail.updatedAt || "",
      });
      if (storeDetail.countryId && !countryApiCalledRef.current) {
        setCountryValue(storeDetail.countryId);
        dispatch(fetchCompanyFormList(storeDetail.countryId));
        countryApiCalledRef.current = true;
      }
    }
  }, [storeDetail, id]);

  const clientValidationHandler = () => {
    const { name, companyId, address, email, phone } = data;

    let errorsObject = {};

    errorsObject = basicFormValidationHandler({
      name,
      companyId,
      countryValue,
    });

    setErrors(errorsObject);

    if (!isEmpty(errorsObject))
      toast.error("Morate popuniti sva obavezna polja", { theme: "colored" });
    return !isEmpty(errorsObject);
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    if (clientValidationHandler()) {
      return;
    }
    if (id) {
      data.id = id;
      dispatch(fetchEditStore(data, navigate));
    } else {
      dispatch(createNewStore(data, navigate));
    }
  };

  const handleChangeCountry = (event) => {
    setCountryValue(event.target.value);
    dispatch(fetchCompanyFormList(event.target.value));
  };

  return (
    <Fragment>
      <Breadcrumb
        breadcrumbItems={breadcrumbItems}
        type={translateModule}
        id={id}
        extraTitle={storeDetail && storeDetail.name}
        pageType={pageType}
      />
      <Container>
        <Row>
          <Col lg={12}>
            <form onSubmit={handleSubmit} noValidate>
              <Row>
                <Col lg={12}>
                  <div className="text-end mb-2">
                    <SubmitButtonForm id={id} />
                  </div>
                </Col>
              </Row>

              <Row>
                <Col lg={9}>
                  <Card>
                    <Card.Header>
                      <FormTitleMain />
                    </Card.Header>
                    <Card.Body>
                      <div className="product-holder-admin-left-side-text">
                        <Row>
                          <Col lg={12} md={12} xs={12} className="mb-4">
                            <InputField
                              label={t("storeForm.name")}
                              name="name"
                              value={data.name}
                              onChange={handleInputChange}
                              error={errors}
                              errorsHandler={setErrors}
                              fullWidth
                              size="small"
                              required
                            />
                          </Col>
                          <Col lg={5} md={12} xs={12} className="mb-4">
                            <FormControl
                              fullWidth
                              size="small"
                              required
                              error={errors?.countryValue}
                              disabled={countryFormList.length === 1}
                            >
                              <InputLabel id="demo-simple-select-label">
                                {t("storeForm.country")}
                              </InputLabel>
                              <Select
                                labelId="demo-simple-select-label"
                                id="demo-simple-select"
                                value={countryValue}
                                label={t("storeForm.country")}
                                onChange={handleChangeCountry}
                                size="small"
                              >
                                {countryFormList?.map((item, key) => (
                                  <MenuItem value={item.id} key={key}>
                                    {item.name}
                                  </MenuItem>
                                ))}
                              </Select>
                              {errors.countryValue && (
                                <FormHelperText>
                                  {t("storeForm.requiredField")}
                                </FormHelperText>
                              )}
                            </FormControl>
                          </Col>
                          <Col lg={7} md={12} xs={12} className="mb-4">
                            <FormControl
                              fullWidth
                              size="small"
                              required
                              error={errors.companyId ? true : false}
                            >
                              <InputLabel id="demo-simple-select-label">
                                {t("storeForm.company")}
                              </InputLabel>
                              <Select
                                labelId="demo-simple-select-label"
                                id="demo-simple-select"
                                value={data.companyId}
                                label={t("storeForm.company")}
                                name="companyId"
                                onChange={handleInputChange}
                                size="small"
                                disabled={countryValue === "" && true}
                              >
                                {companyFormList?.map((item, key) => (
                                  <MenuItem value={item.id} key={key}>
                                    {item.name}
                                  </MenuItem>
                                ))}
                              </Select>
                              {errors.companyId && (
                                <FormHelperText>
                                  {t("storeForm.requiredField")}
                                </FormHelperText>
                              )}
                            </FormControl>
                          </Col>
                          <Col lg={6} md={12} xs={12} className="mb-4">
                            <InputField
                              label={t("storeForm.address")}
                              name="address"
                              value={data.address}
                              onChange={handleInputChange}
                              error={errors}
                              errorsHandler={setErrors}
                              fullWidth
                              size="small"
                            />
                          </Col>
                          <Col lg={6} md={12} xs={12} className="mb-4">
                            <InputField
                              label={t("storeForm.email")}
                              name="email"
                              type="email"
                              value={data.email}
                              onChange={handleInputChange}
                              error={errors}
                              errorsHandler={setErrors}
                              fullWidth
                              size="small"
                            />
                          </Col>
                          <Col lg={6} md={12} xs={12} className="mb-4">
                            <InputField
                              label={t("storeForm.phone")}
                              name="phone"
                              type="text"
                              value={data.phone}
                              onChange={handleInputChange}
                              error={errors}
                              errorsHandler={setErrors}
                              fullWidth
                              size="small"
                            />
                          </Col>
                        </Row>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
                <Col lg={3}>
                  <Row>
                    <Col lg={12}>
                      <SelectStatus
                        data={data}
                        handleInputChange={handleInputChange}
                      />
                    </Col>
                    {id && (
                      <Col lg={12}>
                        <FormSystemInformation data={data} />
                      </Col>
                    )}
                  </Row>
                </Col>
              </Row>
            </form>
          </Col>
        </Row>
      </Container>
    </Fragment>
  );
};

export default StoreAdd;
